import type { ActivityEngagementSeries, ApiResponse, User } from '../types';
import { getErrorMessage } from '../types';
import type { EligibilityDefinition, SubscriptionDefinition, UserSubscription } from '../types/settings';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin, getAuthHeaders, handleApiResponse } from './fetchHelper';
import type { AdminRole } from '../config/adminRoles';
import type { PracticeTreeNodeLike } from '@shared-ui/whats-new-experiences';
import type { UserRole } from './auth';

export interface AdminUserRow {
  id: string;
  name: string;
  email: string;
  phone?: string;
  /** Unified roles — replaces the legacy `adminRoles` field. */
  roles: UserRole[];
  /** Authority as (role, scope): scope is 'platform', '0' (public) or a tenant id. */
  roleAssignments?: RoleGrant[];
  createdAt: string;
}

export interface RoleGrant {
  role: AdminRole;
  scope: string;
}

// Types exported from this module
export interface RateLimitUserSummary {
  userId: string;
  userName: string;
  email: string;
  submissionsLastHour: number;
  llmCallsToday: number;
}

export interface RateLimitStatsResponse {
  users: RateLimitUserSummary[];
  limits: {
    student: { apiPerMinute: number; submissionsPerHour: number; llmPerDay: number };
    teacher: { apiPerMinute: number; submissionsPerHour: number; llmPerDay: number };
    admin: { apiPerMinute: number; submissionsPerHour: number; llmPerDay: number };
  };
  aggregated: { totalSubmissions24h: number; totalLlmCalls24h: number };
}

export interface RateLimitEvent {
  id: string;
  userId: string;
  eventType: 'submission' | 'llm';
  operation: string;
  createdAt: string;
  ttl: number;
  questionId?: string;
  llmProvider?: string;
}

export interface RateLimitUserDetail {
  userId: string;
  userName: string;
  submissionsLastHour: number;
  llmCallsToday: number;
  recentEvents: RateLimitEvent[];
  hourlyBreakdown: { hour: string; submissions: number; llmCalls: number }[];
}

export interface StudentTeacherControlBreakdown {
  subject: string;
  controlName: string;
  systemDefault: boolean;
  teacherDefault?: boolean;
  studentOverride?: boolean;
  resolved: boolean;
  teacherId: string;
  teacherName?: string;
}

export interface StudentTeacherControlsResponse {
  controls: StudentTeacherControlBreakdown[];
}

export interface FeatureGateDefinition {
  enabled: boolean;
  description?: string;
  enabledForRings?: ('devtesting' | 'beta' | 'prod' | 'guest')[];
  allowOverride?: boolean;
  /** Server-stamped — absent on a gate saved before these fields existed. */
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** One notification's rollout in this environment. Same shape as a feature gate's
 *  rollout, deliberately — see notification-registry-and-rollout.md decision 2. */
export interface NotificationRolloutEntry {
  enabled: boolean;
  enabledForRings?: ('devtesting' | 'beta' | 'prod' | 'guest')[];
}

export interface NotificationRow {
  id: string;
  eventType: string;
  label: string;
  description: string;
  category?: string;
  /** Absent ⇒ off. There is no default in code — going live is always something
   *  someone did, recorded here. */
  rollout?: NotificationRolloutEntry;
  ruleExists: boolean;
}

export interface NotificationDriftItem {
  id: string;
  detail: string;
}

export interface NotificationRolloutResponse {
  notifications: NotificationRow[];
  drift: {
    declaredWithoutRule: NotificationDriftItem[];
    ruleWithoutDeclaration: NotificationDriftItem[];
    rolloutForUnknownNotification: NotificationDriftItem[];
    declaredForUnknownEventType: NotificationDriftItem[];
    clean: boolean;
  };
  updatedAt?: string;
  updatedBy?: string;
}

export interface UserFeatureGateView {
  name: string;
  description?: string;
  globalEnabled: boolean;
  /** 'global' = resolved with no user, so this row can never be overridden —
   *  a different reason from a kill switch, and worth saying differently. */
  resolution?: 'per-user' | 'global';
  /** False = kill switch; per-user overrides are ignored for this gate. */
  allowOverride?: boolean;
  userOverride: boolean | null;
  /** The user's tenant's override (tenant-settings), below the user's own. */
  tenantOverride?: boolean | null;
  resolved: boolean;
}

export interface UserExperimentView {
  key: string;
  description?: string;
  enabled: boolean;
  variants: string[];
  bucketed: string | null;
  userOverride: string | null;
  resolved: string | null;
}

export interface ExperimentVariant {
  name: string;
  /** Relative weight; need not sum to 100. Must be > 0 to be selectable. */
  weight: number;
  /** When set, only users in these rings can land in this variant. ANDed with
   *  the experiment-level rings. Empty / unset = every ring. Lets an arm be
   *  rolled out one ring at a time without re-bucketing the other rings. */
  enabledForRings?: ('devtesting' | 'beta' | 'prod' | 'guest')[];
}

export interface ExperimentDefinition {
  enabled: boolean;
  description?: string;
  enabledForRings?: ('devtesting' | 'beta' | 'prod' | 'guest')[];
  variants: ExperimentVariant[];
}

export interface WhatsAppSettings {
  id: 'whatsapp-settings';
  phoneNumberId: string;
  progressReportTemplate?: string;
  otpTemplate?: string;
  otpTemplateLanguage?: string;
  /** E.164 numbers an operational alert pages — the on-call rota, not the
   *  admin list. See WhatsAppSettingsRepository for why it is a number. */
  alertPhones?: string[];
  updatedBy?: string;
  _ts?: number;
}

/**
 * MSG91 configuration as the console is allowed to see it. The auth key is
 * never sent back — only whether one is stored and its last four characters.
 */
export interface SmsSettingsView {
  apiKeySet: boolean;
  apiKeyHint?: string;
  otpTemplateId?: string;
  otpVar?: string;
  updatedBy?: string;
}

export interface SmsSettingsResponse {
  settings: SmsSettingsView | null;
  /** Whether the Azure env vars are still supplying these, for anything the
   *  doc above leaves unset. */
  env: { apiKeySet: boolean; otpTemplateIdSet: boolean };
}

export interface ProviderCostSummary {
  provider: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  costUsd: number;
}

export interface CostPeriod {
  submissions: number;
  llmCalls: number;
  totalCostUsd: number;
  byProvider: ProviderCostSummary[];
}

export interface UserCostsResponse {
  userId: string;
  last7Days: CostPeriod;
  allTime: CostPeriod;
}

/** A visit to the public science site, as the beacons recorded it. */
export interface SiteVisit {
  visitId: string;
  startedAt: string;
  endedAt: string;
  referrerHost?: string;
  events: {
    timestamp: string;
    kind: 'page_view' | 'simulation_open' | 'lab_open' | 'chemistry_lab_session' | 'chemistry_lab_mix';
    path: string;
    entityId?: string;
    /** What a lab act was — the substances of a mix. Recorded by the server. */
    detail?: string;
  }[];
  /** The per-act rows beneath this visit have expired (they are kept 14 days),
   *  so its events were rebuilt from the sitting's running tally: which
   *  surfaces, how many times, in what order — but not the minute each one
   *  happened. Shown as such, because a reconstruction presented as a recording
   *  is worse than a gap. */
  fromTally?: boolean;
}

export interface UserSiteActivityResponse {
  from: string;
  to: string;
  days: number;
  visits: SiteVisit[];
  /** `labUses`: visits with at least one chemistry-bench mix; `labMixes`: the mixes. */
  totals: { visits: number; events: number; pageViews: number; simulationOpens: number; labUses: number; labMixes: number };
}

export interface ImpersonateResponse {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    grade?: number;
    type?: string;
    targetExams?: string[];
    subjects?: string[];
    registrationComplete?: boolean;
  };
}

/** Teacher document shape used by the admin teacher-profile endpoints.
 *  Mirrors the backend TeacherSchema (Backend/functions/src/shared/types.ts).
 *  Kept here rather than re-exported from @padcanvas/shared because admin
 *  console doesn't otherwise depend on the Teacher type. */
export interface AdminTeacherDoc {
  id: string;
  userId: string;
  subjects: string[];
  grades: string[];
  displayName?: string;
  bio?: string;
  photoUrl?: string;
  location?: { city?: string; state?: string; area?: string };
  pastResults?: { year: string; exam: string; description: string }[];
  experience?: string;
  qualifications?: string;
  /** Whether students can find this teacher when choosing a teacher.
   *  Absent means false (private). */
  discoverable?: boolean;
  instructions?: {
    general?: string;
    hint?: string;
    analysis?: string;
    learningMoments?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export const userApi = {
  // Single user by ID
  getUserById: (userId: string) => {
    return fetchWithAdmin<User>(`/console/users/${userId}`);
  },

  // A user's practice tree — backs the What's New experience preview
  // ("view in the context of any user"). Same shape as the student's own tree.
  getUserPracticeTree: (userId: string, subject: string) =>
    fetchWithAdmin<{ nodes: PracticeTreeNodeLike[] }>(
      `/console/users/${userId}/practice-tree?subject=${encodeURIComponent(subject)}`,
    ),

  // A user's latest ready assignment report, exactly as the What's New
  // `assignment-report` gate would build it. `payload: null` = the gate would
  // hide the tip from this user.
  getUserLatestSessionReport: (userId: string, maxAgeDays?: number) =>
    fetchWithAdmin<{ payload: Record<string, unknown> | null }>(
      `/console/users/${userId}/latest-session-report${maxAgeDays ? `?maxAgeDays=${maxAgeDays}` : ''}`,
    ),

  /** One user's day-by-day activity — event counts per kind, zero-filled. */
  getUserActivity: (userId: string, days: number) =>
    fetchWithAdmin<{
      from: string; to: string; days: number; activeDays: number; totalEvents: number;
      series: ActivityEngagementSeries[];
    }>(`/console/users/${userId}/activity?days=${days}`),

  // Users list
  getUsers: (options?: {
    page?: number;
    pageSize?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    role?: string;
    includeDeleted?: boolean;
    rings?: string;             // comma-separated ring list; empty = all rings
    teacherId?: string;
    tenantId?: string;          // super-admin only; ignored for tenant-admin (locked to own)
    tenantRole?: 'member' | 'tenant_admin';
    includeDummies?: boolean;   // add seeded dummy users to the result (default: excluded)
    includeAgents?: boolean;    // add persona-agent users to the result (default: excluded)
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.set('page', String(options.page));
    if (options?.pageSize) params.set('pageSize', String(options.pageSize));
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.role) params.set('role', options.role);
    if (options?.includeDeleted) params.set('includeDeleted', 'true');
    if (options?.rings) params.set('rings', options.rings);
    if (options?.teacherId) params.set('teacherId', options.teacherId);
    if (options?.tenantId) params.set('tenantId', options.tenantId);
    if (options?.tenantRole) params.set('tenantRole', options.tenantRole);
    if (options?.includeDummies) params.set('includeDummies', 'true');
    if (options?.includeAgents) params.set('includeAgents', 'true');
    const qs = params.toString();
    return fetchWithAdmin<{ items: User[]; total: number; page: number; pageSize: number }>(
      `/console/users${qs ? `?${qs}` : ''}`
    );
  },

  softDeleteUser: async (userId: string): Promise<{ userId: string; deletedAt: string }> => {
    const response = await fetch(`${API_BASE}/console/users/${userId}/soft-delete`, {
      method: 'POST',
      headers: getAuthHeaders('application/json'),
    });
    return handleApiResponse<{ userId: string; deletedAt: string }>(response);
  },

  /**
   * Generic admin PATCH of a whitelisted set of user fields. Send only the
   * fields that changed; pass `pipelineConfig: null` to clear that override.
   */
  updateUser: async (
    userId: string,
    patch: {
      ring?: string;
      bypassSubscription?: boolean;
      name?: string;
      /** E.164 (+919876543210); null or '' clears it. */
      phone?: string | null;
      grade?: number | null;
      targetExams?: string[];
      subjects?: string[];
      pipelineConfig?: string | null;
      /** How this student's handwriting should be READ; null or '' clears it. */
      handwritingNotes?: string | null;
      notificationEmail?: string | null;
      /** Full replacement of the user's editable roles. Includes student/teacher
       *  app personas and all admin roles. Server rejects super_admin. */
      roles?: Exclude<UserRole, 'super_admin'>[];
    },
  ): Promise<{ userId: string; updated: typeof patch }> => {
    const response = await fetch(`${API_BASE}/console/users/${userId}`, {
      method: 'PATCH',
      headers: getAuthHeaders('application/json'),
      body: JSON.stringify(patch),
    });
    return handleApiResponse<{ userId: string; updated: typeof patch }>(response);
  },

  /** Teacher document shape returned by /teacher/profile. Mirrors the
   *  backend TeacherSchema (Backend/functions/src/shared/types.ts). */
  // (Local declaration kept inside the userApi object section is awkward —
  // see `AdminTeacherDoc` defined outside this const below.)

  /** Fetch a teacher's profile (admin-only path via ?targetUserId). */
  getTeacherProfile: (userId: string) =>
    fetchWithAdmin<AdminTeacherDoc>(`/teacher/profile?targetUserId=${encodeURIComponent(userId)}`),

  /** Update a teacher's profile on behalf of the teacher (admin override). */
  updateTeacherProfile: (userId: string, updates: Partial<AdminTeacherDoc>) =>
    fetchWithAdmin<AdminTeacherDoc>(`/teacher/profile`, {
      method: 'PUT',
      body: JSON.stringify({ ...updates, targetUserId: userId }),
    }),

  restoreUser: async (userId: string): Promise<{ userId: string; restoredAt: string }> => {
    const response = await fetch(`${API_BASE}/console/users/${userId}/restore`, {
      method: 'POST',
      headers: getAuthHeaders('application/json'),
    });
    return handleApiResponse<{ userId: string; restoredAt: string }>(response);
  },

  deleteUser: async (userId: string): Promise<{ deletedUserId: string; deletedCounts: Record<string, number> }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deletedUserId: string; deletedCounts: Record<string, number> }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as { deletedUserId: string; deletedCounts: Record<string, number> };
  },

  // Admin users
  getAdminUsers: () =>
    fetchWithAdmin<{ users: AdminUserRow[]; total: number }>('/console/admin-users'),

  createAdminUser: async (user: { name: string; email: string; password: string; phone?: string; roles?: AdminRole[] }): Promise<AdminUserRow> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/admin-users`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<AdminUserRow> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  updateAdminUser: async (userId: string, user: { name?: string; email?: string; phone?: string; roles?: AdminRole[] }): Promise<AdminUserRow> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/admin-users/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(user),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<AdminUserRow> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  resetAdminPassword: async (userId: string, password: string): Promise<{ success: boolean }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/admin-users/${userId}/reset-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ success: boolean }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  /** Full replacement of an admin's scoped grants. Super-admin only. */
  setAdminRoleAssignments: async (userId: string, assignments: RoleGrant[]): Promise<AdminUserRow> => {
    const response = await fetch(`${API_BASE}/console/admin-users/${userId}/role-assignments`, {
      method: 'PUT',
      headers: getAuthHeaders('application/json'),
      body: JSON.stringify({ assignments }),
    });
    return handleApiResponse<AdminUserRow>(response);
  },

  deleteAdminUser: async (userId: string): Promise<{ deleted: string }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/admin-users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ deleted: string }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  // Eligibility
  getEligibilityDefinitions: () =>
    fetchWithAdmin<EligibilityDefinition[]>('/console/eligibility'),

  updateEligibilityDefinitions: async (eligibilities: EligibilityDefinition[]): Promise<EligibilityDefinition[]> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/eligibility`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ eligibilities }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<EligibilityDefinition[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data as EligibilityDefinition[];
  },

  getUserEligibility: (userId: string) =>
    fetchWithAdmin<{
      userId: string;
      subjectEligibilities: {
        subject: string;
        eligibilities: {
          name: string;
          displayName: string;
          description: string;
          enableValues: string[];
          defaultValue: string;
          currentValue: string;
          isSubscriptionDerived?: boolean;
          usage?: { threshold: number | null; used: number; validUntil: string | null; windowStart?: string | null };
          resetPeriod?: string;
        }[];
      }[];
    }>(`/console/users/${userId}/eligibility`),

  // Subscriptions
  getSubscriptionDefinitions: () =>
    fetchWithAdmin<SubscriptionDefinition[]>('/console/subscriptions'),

  updateSubscriptionDefinitions: async (subscriptions: SubscriptionDefinition[]): Promise<SubscriptionDefinition[]> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/subscriptions`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscriptions }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<SubscriptionDefinition[]> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  getUserSubscriptions: (userId: string) =>
    fetchWithAdmin<UserSubscription[]>(`/console/users/${userId}/subscriptions`),

  assignUserSubscription: async (userId: string, body: { subscriptionId: string; startDate: string; endDate: string; subjects?: string[] }): Promise<UserSubscription> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/users/${userId}/subscriptions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<UserSubscription> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  cancelUserSubscription: async (userId: string, subId: string): Promise<void> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/users/${userId}/subscriptions/${subId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{ cancelled: boolean }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
  },

  // Feature Gates
  getFeatureGates: () =>
    fetchWithAdmin<{ gates: Record<string, FeatureGateDefinition> }>('/console/feature-gates'),

  updateFeatureGates: (gates: Record<string, FeatureGateDefinition>) =>
    fetchWithAdmin<{ gates: Record<string, FeatureGateDefinition> }>('/console/feature-gates', {
      method: 'PUT',
      body: JSON.stringify({ gates }),
    }),

  getFeatureGateOverrides: (name: string) =>
    fetchWithAdmin<{ total: number; on: number; off: number }>(
      `/console/feature-gates/${encodeURIComponent(name)}/overrides`,
    ),

  // Notification rollout — which declared notifications reach which rings.
  // See docs/FeatureDesigns/notification-registry-and-rollout.md.
  getNotificationRollout: () =>
    fetchWithAdmin<NotificationRolloutResponse>('/system/notification-rollout'),

  updateNotificationRollout: (notifications: Record<string, NotificationRolloutEntry>) =>
    fetchWithAdmin<{ ok: true; notifications: Record<string, NotificationRolloutEntry> }>(
      '/system/notification-rollout',
      { method: 'PUT', body: JSON.stringify({ notifications }) },
    ),

  // Experiments (A/B variant assignment)
  getExperiments: () =>
    fetchWithAdmin<{ experiments: Record<string, ExperimentDefinition> }>('/console/experiments'),

  updateExperiments: (experiments: Record<string, ExperimentDefinition>) =>
    fetchWithAdmin<{ experiments: Record<string, ExperimentDefinition> }>('/console/experiments', {
      method: 'PUT',
      body: JSON.stringify({ experiments }),
    }),

  getUserFeatureGates: (userId: string) =>
    fetchWithAdmin<{ userId: string; tenantId?: string; gates: UserFeatureGateView[] }>(`/console/users/${userId}/feature-gates`),

  updateUserFeatureGates: (userId: string, featureGates: Record<string, boolean>) =>
    fetchWithAdmin<{ userId: string; featureGates: Record<string, boolean> }>(`/console/users/${userId}/feature-gates`, {
      method: 'PUT',
      body: JSON.stringify({ featureGates }),
    }),

  getUserExperiments: (userId: string) =>
    fetchWithAdmin<{ userId: string; experiments: UserExperimentView[] }>(`/console/users/${userId}/experiments`),

  updateUserExperiments: (userId: string, experimentOverrides: Record<string, string>) =>
    fetchWithAdmin<{ userId: string; experimentOverrides: Record<string, string> }>(`/console/users/${userId}/experiments`, {
      method: 'PUT',
      body: JSON.stringify({ experimentOverrides }),
    }),

  // WhatsApp settings (non-secret config; secrets stay in Azure env vars)
  getWhatsAppSettings: () =>
    fetchWithAdmin<{
      settings: WhatsAppSettings | null;
      supportedProgressReportTemplates: { name: string; label: string }[];
    }>('/console/settings/whatsapp'),

  updateWhatsAppSettings: (payload: { phoneNumberId: string; progressReportTemplate?: string; otpTemplate?: string; otpTemplateLanguage?: string; alertPhones?: string[] }) =>
    fetchWithAdmin<{ settings: WhatsAppSettings }>('/console/settings/whatsapp', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // MSG91 SMS settings. The auth key is write-only: a GET never returns it,
  // and a PUT with a blank key leaves the stored one alone.
  getSmsSettings: () =>
    fetchWithAdmin<SmsSettingsResponse>('/console/settings/sms'),

  updateSmsSettings: (payload: { apiKey?: string; otpTemplateId?: string; otpVar?: string }) =>
    fetchWithAdmin<SmsSettingsResponse>('/console/settings/sms', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // User logging config
  getUserLoggingConfig: (userId: string) =>
    fetchWithAdmin<{
      userId: string;
      loggingConfig: {
        retentionDays?: number;
        providers?: Record<string, {
          enabled?: boolean;
          defaultLevel?: number;
          componentLevels?: Record<string, number>;
        }>;
      } | null;
    }>(`/console/users/${userId}/logging-config`),

  updateUserLoggingConfig: async (userId: string, loggingConfig: {
    retentionDays?: number;
    providers?: Record<string, {
      enabled?: boolean;
      defaultLevel?: number;
      componentLevels?: Record<string, number>;
    }>;
  } | null): Promise<{
    userId: string;
    loggingConfig: {
      retentionDays?: number;
      providers?: Record<string, {
        enabled?: boolean;
        defaultLevel?: number;
        componentLevels?: Record<string, number>;
      }>;
    } | null;
  }> => {
    const token = authApi.getToken();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch(`${API_BASE}/console/users/${userId}/logging-config`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ loggingConfig }),
    });

    if (response.status === 401) {
      authApi.logout();
      throw new Error('Session expired. Please login again.');
    }

    const data: ApiResponse<{
      userId: string;
      loggingConfig: {
        retentionDays?: number;
        providers?: Record<string, {
          enabled?: boolean;
          defaultLevel?: number;
          componentLevels?: Record<string, number>;
        }>;
      } | null;
    }> = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(getErrorMessage(data.error) || `API error: ${response.status}`);
    }
    return data.data!;
  },

  // Teacher Controls (Admin read-only)
  getStudentTeacherControls: (userId: string) =>
    fetchWithAdmin<StudentTeacherControlsResponse>(`/console/users/${userId}/teacher-controls`),

  // User costs
  getUserCosts: (userId: string) =>
    fetchWithAdmin<UserCostsResponse>(`/console/users/${userId}/costs`),

  // What this person did on the public science site. Separate from Sessions:
  // the science site posts beacons, never session traces.
  getUserSiteActivity: (userId: string, days = 30) =>
    fetchWithAdmin<UserSiteActivityResponse>(`/console/users/${userId}/site-activity?days=${days}`),

  // Usage stats
  getUsageStats: () => fetchWithAdmin<RateLimitStatsResponse>('/console/usage-stats'),

  getRateLimitUserDetail: (userId: string) =>
    fetchWithAdmin<RateLimitUserDetail>(`/console/usage-stats/${userId}`),

  // Impersonation
  impersonateUser: (userId: string) =>
    fetchWithAdmin<ImpersonateResponse>('/console/impersonate', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  // Send a direct message to one user — delivered as a published, one-user
  // What's New card (see docs/FeatureDesigns/admin-direct-user-message.md).
  sendUserMessage: (userId: string, message: { title?: string; body?: string; imageUrl?: string }) =>
    fetchWithAdmin<{ id: string }>(`/console/users/${userId}/message`, {
      method: 'POST',
      body: JSON.stringify(message),
    }),

  /** Super-admin only. By id, so it reaches accounts with no email too. */
  setUserPassword: (userId: string, password: string) =>
    fetchWithAdmin<{ success: true }>(`/console/users/${userId}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),

  // Tenant invites — invite a user to a tenant (creates pending row + emails magic link)
  inviteTenantMember: (body: {
    email: string;
    name?: string;
    tenantId: string;
    /** Role to grant on invite. `super_admin` is intentionally excluded
     *  (server-side rejection in InvitableRoleSchema). */
    role: 'student' | 'teacher' | 'tenant_admin' | 'content_admin' | 'prompt_admin' | 'eval_admin' | 'user_admin';
  }) =>
    fetchWithAdmin<{ userId: string; isNewUser: boolean; emailSent: boolean }>('/console/tenant-invites', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Diagnosis
  listDiagnosisGroups: () =>
    fetchWithAdmin<{ groups: { id: string; name: string; incremental?: boolean; cap?: number; params: { key: string; label: string; type: string; default: number; min?: number; max?: number }[]; checks: { id: string; name: string; lastResult?: { name: string; status: 'pass' | 'fail'; count: number; checkedCount?: number; newIssueCount?: number; items?: unknown[]; executedOn?: string } }[] }[] }>('/console/diagnosis'),

  runDiagnosisGroup: (groupId: string, checkIds: string[], params?: Record<string, string>) => {
    const qs = new URLSearchParams({ group: groupId, checks: checkIds.join(','), ...params }).toString();
    return fetchWithAdmin<{ groupId: string; checks: { name: string; status: 'pass' | 'fail'; count: number; checkedCount?: number; newIssueCount?: number; items?: unknown[]; executedOn?: string }[]; progress?: { scanned: number; cursorTs: number; reachedEnd: boolean; cap: number; reset: boolean } }>(`/console/diagnosis?${qs}`, {
      method: 'POST',
    });
  },
};
